/*
 * Figma `117:118` — SELL-02 · Ticket Delivery · Mode + Channel Readiness.
 *
 * The frozen frame is an editor with a consequence rail, so it composes `EditorTemplate`: the mode
 * choice and the copy channels on the left, and on the right what the buyer will experience, what
 * the change does not do, and the save bar.
 *
 * Two things the old panel could not say, and the reason this screen is worth rebuilding rather
 * than restyling:
 *
 * - **Readiness per channel.** `channelAvailability` was a sentence appended to a hint. Here each
 *   channel states whether it can actually send right now, because ticking a channel the platform
 *   has switched off is how an organizer concludes copies are going out when none are.
 * - **What changing the mode does to orders already sold.** It does nothing to them — the settings
 *   are stamped on each order at creation — and that is precisely the thing an organizer needs to
 *   know *before* saving, not after wondering whether they just resent five hundred tickets.
 *
 * The mode options stay a radio group. The frame draws three cards, but exactly one is in effect and
 * a radio group is what that means; arrow-key movement between them comes free with it.
 *
 * Copy note: the frame's labels are short ("ส่งหลังตรวจสอบช่องทาง"). The production copy is kept,
 * because it states the trade — a mode that removes a verification step is a real reduction in how
 * sure we are that the person holding the screen is the person who paid, and the organizer choosing
 * it should be choosing it knowingly. The frame's hierarchy is followed; its shorthand is not.
 */
import { Alert, Card, EditorTemplate, SaveBar, StatusBadge, Toggle } from '..';
import type { SaveState } from '..';
import type { Destination } from '../destinations';
import type { DeliveryChannel, TicketDeliveryMode } from '../../api';
import { useTicketDelivery } from './useTicketDelivery';
import type { TicketDeliveryState } from './useTicketDelivery';

const MODES: { key: TicketDeliveryMode; title: string; body: string; badge?: string }[] = [
  {
    key: 'verified_channel',
    title: 'ให้ยืนยันช่องทางก่อนจึงเห็นตั๋ว',
    body: 'ผู้ซื้อเลือก LINE / SMS / อีเมล แล้วยืนยันว่าเป็นเจ้าของช่องทางนั้น ตั๋วจึงจะแสดงและถูกส่งออกไป ปลอดภัยที่สุด แต่ผู้ซื้อบางส่วนหลุดระหว่างทาง',
    badge: 'ค่าเริ่มต้น',
  },
  {
    key: 'instant',
    title: 'แสดงตั๋วบนหน้าจอทันที',
    body: 'ผู้ซื้อเห็น QR ทันทีที่สลิปผ่าน และระบบไม่ส่งอะไรออกไปเลย — ไม่มีค่าส่ง SMS ต่อออเดอร์ ผู้ซื้อกลับมาดูตั๋วภายหลังได้ที่ /myticket',
  },
  {
    key: 'instant_and_send',
    title: 'แสดงตั๋วทันที และส่งสำเนาให้ด้วย',
    body: 'ผู้ซื้อเห็นตั๋วบนหน้าจอก่อน แล้วระบบส่งลิงก์ตั๋วตามช่องทางที่เลือกไว้ด้านล่างเป็นสำเนา ข้อความที่ส่งจะใช้ถ้อยคำแบบ “สำเนาบัตร” ไม่ใช่ “ชำระเงินสำเร็จ”',
  },
];

const CHANNELS: { key: DeliveryChannel; label: string; hint: string }[] = [
  { key: 'line', label: 'JamTick LINE', hint: 'ส่งได้เฉพาะออเดอร์ที่เคยผูกบัญชี LINE ไว้แล้ว' },
  { key: 'sms', label: 'SMS', hint: 'ถึงแน่นอนที่สุด แต่มีค่าส่งต่อข้อความ' },
  { key: 'email', label: 'อีเมล', hint: 'ไม่มีค่าส่ง และผู้ซื้อค้นย้อนหลังได้ง่าย' },
];

/** What the buyer goes through, written from the mode that is currently chosen. */
function buyerJourney(mode: TicketDeliveryMode, channels: DeliveryChannel[], portal: boolean): string[] {
  const names = CHANNELS.filter((c) => channels.includes(c.key)).map((c) => c.label);
  if (mode === 'verified_channel') {
    return [
      'ชำระเงินสำเร็จ และระบบสร้างบัตรกับ QR',
      'ผู้ซื้อเลือกช่องทางและยืนยันว่าเป็นเจ้าของ',
      'หน้า Order Status แสดงว่า “รอยืนยันช่องทาง”',
      'ยืนยันผ่านแล้วจึงเปิด QR ได้ และระบบส่งตั๋วไปทางนั้น',
      'ยืนยันไม่สำเร็จ ผู้ซื้อยังไม่เห็น QR — เจ้าหน้าที่ช่วยได้จากหน้าออร์เดอร์',
    ];
  }
  if (mode === 'instant') {
    return [
      'ชำระเงินสำเร็จ และระบบสร้างบัตรกับ QR',
      'หน้า Order Status เปิด QR ทันที ไม่ต้องรอ provider',
      'ระบบไม่ส่งข้อความใด ๆ ออกไป',
      portal
        ? 'ปิดหน้าจอไปแล้วกลับมาดูตั๋วได้ที่ /myticket'
        : 'ปิดหน้าจอไปแล้วไม่มีทางกลับมาหาตั๋ว — /myticket ปิดอยู่ทั้งแพลตฟอร์ม',
    ];
  }
  return [
    'ชำระเงินสำเร็จ และระบบสร้างบัตรกับ QR',
    'หน้า Order Status เปิด QR ทันที',
    names.length ? `ระบบส่งสำเนาไปทาง ${names.join(' และ ')}` : 'ยังไม่ได้เลือกช่องทาง จึงไม่มีสำเนาถูกส่งออกไป',
    'ช่องทางใดส่งไม่สำเร็จ ระบบ retry โดยไม่สร้างบัตรซ้ำ',
  ];
}

export function TicketDeliveryScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: TicketDeliveryState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    data, mode, channels, loading, saving, saved, error, loadError,
    dirty, sendMode, blocked, setMode, toggleChannel, save, reset,
  } = state;

  const saveState: SaveState =
    error ? 'error' : saving ? 'saving' : dirty ? 'dirty' : saved ? 'saved' : 'clean';

  return (
    <EditorTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      sections={
        <>
          <Card title="ผู้ซื้อจะได้รับตั๋วเมื่อไร" sub="มีผลกับออร์เดอร์ใหม่ของอีเวนต์นี้เท่านั้น">
            <div className="jt-choice-group" role="radiogroup" aria-label="ช่วงเวลาที่ผู้ซื้อเห็นตั๋ว">
              {MODES.map((option) => (
                <label key={option.key} className="jt-choice" data-active={mode === option.key || undefined}>
                  <input
                    type="radio"
                    name="ticket-delivery-mode"
                    className="jt-choice-input"
                    checked={mode === option.key}
                    onChange={() => setMode(option.key)}
                  />
                  <span className="jt-choice-copy">
                    <span className="jt-choice-title">
                      {option.title}
                      {option.badge && <StatusBadge tone="neutral">{option.badge}</StatusBadge>}
                    </span>
                    <span className="jt-choice-body">{option.body}</span>
                  </span>
                </label>
              ))}
            </div>
          </Card>

          <Card
            title="ช่องทางสำเนา"
            sub={sendMode
              ? 'ส่งลิงก์ตั๋วตามช่องทางที่เปิดไว้ หลังผู้ซื้อเห็นตั๋วบนหน้าจอแล้ว'
              : 'โหมดที่เลือกอยู่ไม่ส่งสำเนา — ตัวเลือกด้านล่างถูกเก็บไว้ แต่จะยังไม่ถูกใช้'}
          >
            {CHANNELS.map((channel) => {
              const available = data?.channelAvailability[channel.key] ?? false;
              return (
                <Toggle
                  key={channel.key}
                  label={channel.label}
                  checked={channels.includes(channel.key)}
                  disabled={!sendMode}
                  onChange={() => toggleChannel(channel.key)}
                  consequence={channel.hint}
                  trailing={
                    <StatusBadge tone={available ? 'success' : 'warning'}>
                      {available ? 'พร้อมส่ง' : 'ปิดที่แพลตฟอร์ม'}
                    </StatusBadge>
                  }
                />
              );
            })}

            {sendMode && channels.length === 0 && (
              <Alert tone="warning" title="ยังไม่ได้เลือกช่องทาง">
                โหมดนี้จะทำงานเหมือน “แสดงตั๋วทันที” คือไม่ส่งอะไรออกไปเลย
              </Alert>
            )}
            {blocked.length > 0 && (
              <Alert tone="warning" title="ช่องทางที่เลือกไว้ยังส่งไม่ได้">
                {CHANNELS.filter((c) => blocked.includes(c.key)).map((c) => c.label).join(' และ ')}
                {' '}ถูกปิดอยู่ที่ระดับแพลตฟอร์ม — เลือกไว้ได้ แต่ตอนนี้จะยังไม่มีอะไรถูกส่งออกไปทางช่องทางนั้น
              </Alert>
            )}
            {mode === 'instant' && !data?.myTicketPortalEnabled && (
              <Alert tone="danger" title="ผู้ซื้อจะไม่มีทางกลับมาหาตั๋ว">
                โหมดนี้ไม่ส่งข้อความใด ๆ และหน้า <code className="jt-mono">/myticket</code> ปิดอยู่ทั้งแพลตฟอร์ม
                {' '}— ถ้าผู้ซื้อปิดหน้าจอไปแล้วจะไม่มีทางกลับมาดูตั๋วได้เลย ติดต่อผู้ดูแลระบบให้เปิดก่อน
              </Alert>
            )}
          </Card>
        </>
      }
      preview={
        <>
          <Card title="ผู้ซื้อจะเจออะไร" sub="อ่านจากโหมดที่เลือกอยู่ตอนนี้">
            <ol className="jt-journey">
              {buyerJourney(mode, channels, data?.myTicketPortalEnabled ?? false).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </Card>
          <Card title="สิ่งที่การบันทึกนี้ไม่ทำ">
            <ul className="jt-consequences">
              <li>ออร์เดอร์ที่ขายไปแล้วไม่เปลี่ยน — ค่าที่ใช้ถูกบันทึกไว้กับออร์เดอร์ตั้งแต่ตอนสร้าง</li>
              <li>ตั๋วที่ส่งออกไปแล้วไม่ถูกเรียกคืน และจะไม่ถูกส่งซ้ำ</li>
              <li>การเปิดหรือปิด provider ที่ระดับแพลตฟอร์มไม่ได้แก้จากหน้านี้</li>
            </ul>
          </Card>
        </>
      }
      stickyActions={
        <SaveBar
          state={saveState}
          error={error || null}
          onSave={save}
          onDiscard={dirty ? reset : undefined}
          consequence="มีผลกับออร์เดอร์ใหม่เท่านั้น ออร์เดอร์ที่ส่งแล้วไม่ถูกย้อนกลับ"
        />
      }
    />
  );
}

/**
 * The screen with its own data attached — what a shell renders.
 *
 * Kept separate from the presentation above so the screen can be rendered in a test from a plain
 * state object, without a fetch, which is how its states get pinned at all.
 */
export function TicketDeliveryDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useTicketDelivery(token, eventSlug);
  return <TicketDeliveryScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
